/**
 * Ed Hardy Swim — offer ladders.
 *
 * Two offers run side by side:
 *   dresses  2 = 15% off, 3 = 20% off, 4th free
 *   bikinis  second piece of a set 10% off
 *
 * This module owns the progress meter shown on dress pages and in the cart,
 * and auto-applies the best qualifying discount code. Codes are configured in
 * the theme editor; with no code set the meter still guides the customer but
 * makes no saving claim, and any code that fails to apply is dropped rather
 * than displayed as a fake discount.
 */

import { loadCatalog, findCounterparts } from '@eh/catalog';

const root = () => window.Shopify?.routes?.root || '/';

/** Tiers are evaluated high to low; the first match wins. */
function tiersFrom(dataset) {
  return [
    { qty: 4, label: dataset.t4Label || '4th dress free', code: (dataset.t4Code || '').trim() },
    { qty: 3, label: dataset.t3Label || '20% off 3 dresses', code: (dataset.t3Code || '').trim() },
    { qty: 2, label: dataset.t2Label || '15% off 2 dresses', code: (dataset.t2Code || '').trim() },
  ];
}

async function dressCountInCart(catalog) {
  const cart = await fetch(`${root()}cart.js`).then((r) => r.json());
  const byHandle = new Map(catalog.map((p) => [p.handle, p]));
  let count = 0;
  for (const item of cart.items || []) {
    const p = byHandle.get(item.handle);
    if (p && p.cat === 'dress') count += item.quantity;
  }
  return { count, cart };
}

class EhOffer extends HTMLElement {
  async connectedCallback() {
    this.tiers = tiersFrom(this.dataset);
    this.bar = this.querySelector('[data-eh-bar]');
    this.text = this.querySelector('[data-eh-text]');

    this.catalog = await loadCatalog();
    await this.refresh();

    /* Horizon fires these when the cart drawer updates. */
    ['cart:refresh', 'cart:update', 'variant:update'].forEach((ev) =>
      document.addEventListener(ev, () => this.refresh())
    );
  }

  async refresh() {
    let count = 0;
    try {
      ({ count } = await dressCountInCart(this.catalog));
    } catch (e) {
      /* cart unreachable — show the ladder with nothing earned */
    }

    const earned = this.tiers.find((t) => count >= t.qty);
    const next = [...this.tiers].reverse().find((t) => count < t.qty);

    const target = next ? next.qty : this.tiers[0].qty;
    const pct = Math.min(100, Math.round((count / target) * 100));
    this.bar.style.setProperty('--eh-progress', `${pct}%`);

    if (earned) {
      this.text.innerHTML = `<strong>${earned.label}</strong> unlocked${
        next ? ` — add ${next.qty - count} more for ${next.label.toLowerCase()}` : ''
      }`;
      this.classList.add('is-earned');
      if (earned.code) this.apply(earned.code);
    } else if (next) {
      const need = next.qty - count;
      this.text.innerHTML = `Add ${need} more dress${need === 1 ? '' : 'es'} for <strong>${next.label.toLowerCase()}</strong>`;
      this.classList.remove('is-earned');
    }
  }

  /** Apply a discount code, then verify it actually landed. */
  async apply(code) {
    if (this._applied === code) return;
    this._applied = code;
    try {
      await fetch(`${root()}discount/${encodeURIComponent(code)}`, {
        credentials: 'same-origin',
      });
      const cart = await fetch(`${root()}cart.js`).then((r) => r.json());
      if (!(cart.total_discount > 0)) {
        this._applied = null;
        this.dataset.codeFailed = 'true';
      }
    } catch (e) {
      this._applied = null;
    }
  }
}

if (!customElements.get('eh-offer')) customElements.define('eh-offer', EhOffer);

/* ------------------------------------------------------------------ */
/* Cart drawer upsell: offer the matching piece for any half-set in the */
/* cart, so a lone bikini top prompts its bottom.                       */
/* ------------------------------------------------------------------ */

class EhCartUpsell extends HTMLElement {
  async connectedCallback() {
    this.list = this.querySelector('[data-eh-upsell-list]');
    this.catalog = await loadCatalog();
    this.render();
    ['cart:refresh', 'cart:update'].forEach((ev) =>
      document.addEventListener(ev, () => this.render())
    );
  }

  async render() {
    let cart;
    try {
      cart = await fetch(`${root()}cart.js`).then((r) => r.json());
    } catch (e) {
      return;
    }

    const inCart = new Set((cart.items || []).map((i) => i.handle));
    const byHandle = new Map(this.catalog.map((p) => [p.handle, p]));
    const suggestions = [];
    const seen = new Set();

    for (const item of cart.items || []) {
      const p = byHandle.get(item.handle);
      if (!p) continue;
      for (const mate of findCounterparts(p, this.catalog)) {
        if (inCart.has(mate.handle) || seen.has(mate.handle) || mate.soldOut) continue;
        seen.add(mate.handle);
        suggestions.push(mate);
      }
    }

    this.hidden = suggestions.length === 0;
    if (this.hidden) return;

    this.list.innerHTML = suggestions
      .slice(0, 3)
      .map((p) => {
        const v = p.variants.find((x) => x.available);
        return `
        <li class="eh-upsell__item">
          <a class="eh-upsell__thumb" href="${root()}products/${p.handle}">
            <img src="${p.image.replace(/(\.(jpg|jpeg|png|webp))(\?|$)/i, '_120x$1$3')}" alt="" loading="lazy" width="60" height="75">
          </a>
          <div class="eh-upsell__body">
            <p class="eh-upsell__name">${p.title}</p>
            <p class="eh-upsell__meta">Completes your set</p>
          </div>
          <a class="eh-upsell__btn" href="${root()}products/${p.handle}">Pick size</a>
        </li>`;
      })
      .join('');
  }
}

if (!customElements.get('eh-cart-upsell')) customElements.define('eh-cart-upsell', EhCartUpsell);

/* ------------------------------------------------------------------ */
/* Collection-level mix-and-match ladder, for categories with no set to */
/* complete (beach bags).                                              */
/* ------------------------------------------------------------------ */

class EhCollectionOffer extends HTMLElement {
  async connectedCallback() {
    this.cats = (this.dataset.cats || '')
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);
    this.noun = this.dataset.noun || 'item';
    this.tiers = [
      {
        qty: parseInt(this.dataset.t3Qty || '3', 10),
        label: this.dataset.t3Label || '25% off',
        code: (this.dataset.t3Code || '').trim(),
      },
      {
        qty: parseInt(this.dataset.t2Qty || '2', 10),
        label: this.dataset.t2Label || '15% off',
        code: (this.dataset.t2Code || '').trim(),
      },
    ];
    this.bar = this.querySelector('[data-eh-bar]');
    this.text = this.querySelector('[data-eh-text]');

    this.catalog = await loadCatalog();

    /* Self-scoping: this bar lives on the shared collection template, so it
       hides itself unless the collection being viewed is actually mostly the
       category it advertises. Saves needing an alternate template assigned in
       admin for every collection. */
    if (!(await this.relevant())) return;
    this.hidden = false;

    await this.refresh();
    ['cart:refresh', 'cart:update'].forEach((ev) =>
      document.addEventListener(ev, () => this.refresh())
    );
  }

  async relevant() {
    const handle = this.dataset.collection || document.body.dataset.ehCollection || '';
    if (!handle) return false;
    try {
      const r = await fetch(`${root()}collections/${handle}/products.json?limit=250`);
      if (!r.ok) return false;
      const d = await r.json();
      const handles = new Set((d.products || []).map((p) => p.handle));
      const members = this.catalog.filter((p) => handles.has(p.handle));
      if (!members.length) return false;
      const hits = members.filter((p) => this.cats.includes(p.cat)).length;
      return hits / members.length >= 0.6;
    } catch (e) {
      return false;
    }
  }

  async refresh() {
    let count = 0;
    try {
      const cart = await fetch(`${root()}cart.js`).then((r) => r.json());
      const byHandle = new Map(this.catalog.map((p) => [p.handle, p]));
      for (const item of cart.items || []) {
        const p = byHandle.get(item.handle);
        if (p && this.cats.includes(p.cat)) count += item.quantity;
      }
    } catch (e) {
      /* cart unreachable — show the ladder with nothing earned */
    }

    const earned = this.tiers.find((t) => count >= t.qty);
    const next = [...this.tiers].reverse().find((t) => count < t.qty);
    const target = next ? next.qty : this.tiers[0].qty;
    this.bar.style.setProperty('--eh-progress', `${Math.min(100, Math.round((count / target) * 100))}%`);

    const plural = (n) => `${n} ${this.noun}${n === 1 ? '' : 's'}`;

    if (earned) {
      this.classList.add('is-earned');
      this.text.innerHTML = `<strong>${earned.label}</strong> unlocked${
        next ? ` — add ${plural(next.qty - count)} more for ${next.label}` : ''
      }`;
      if (earned.code) this.apply(earned.code);
    } else if (next) {
      this.classList.remove('is-earned');
      this.text.innerHTML = `Add ${plural(next.qty - count)} for <strong>${next.label}</strong>`;
    }
  }

  async apply(code) {
    if (this._applied === code) return;
    this._applied = code;
    try {
      await fetch(`${root()}discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' });
      const cart = await fetch(`${root()}cart.js`).then((r) => r.json());
      if (!(cart.total_discount > 0)) this._applied = null;
    } catch (e) {
      this._applied = null;
    }
  }
}

if (!customElements.get('eh-collection-offer')) {
  customElements.define('eh-collection-offer', EhCollectionOffer);
}
